import {
  isActiveDraftComplete,
  isReplyingToFollowUp,
  resolveActiveDraft,
} from './shared-responder-v2-core-follow-up-reply.helpers';
import {
  selectCurrentSpec,
  selectCurrentThread,
  selectIsBlockedBySiteGate,
} from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import { isOtherTextMissing } from './shared-responder-v2-core-thread-choice';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import { THREAD_STATUSES } from './shared-responder-v2-core-thread.consts';
import type {
  ResponderAnswer,
  ResponderThread,
} from './shared-responder-v2-core-thread.types';
import { selectIsAwaitingVoiceFollowUpReply } from './shared-responder-v2-core-voice-cluster.selectors';

export interface ForwardEdit {
  readonly messageId: string;
  readonly isBubbleRewrite: boolean;
}

export type ForwardIntent =
  | {
      readonly type: 'turn';
      readonly questionId: string;
      readonly threadId: string | null;
      readonly answer: ResponderAnswer;
      readonly requestSeq: number;
      readonly edit: ForwardEdit | null;
    }
  | {
      readonly type: 'skip';
      readonly questionId: string;
      readonly threadId: string | null;
      readonly requestSeq: number;
    }
  | { readonly type: 'advance' };

/**
 * Whether the on-screen question was already sent, so Continue carries nothing new. Editing is
 * the exception: an edit must still produce a turn, since treating the old answer as final
 * skips `applyEditSubmission` and the new draft is neither committed nor posted. A resumed
 * question counts as sent with no local answer, since its history is the backend's proof.
 */
function isAlreadySubmitted(
  state: ResponderSessionState,
  thread: ResponderThread
): boolean {
  if (
    state.editing.mode === 'editing' &&
    state.editing.questionId === thread.questionId
  ) {
    return false;
  }

  return thread.answer !== null || thread.status === THREAD_STATUSES.Closed;
}

/**
 * The reply to an open follow-up as a `turn`; with nothing typed/dictated, `skip` on the voice
 * cluster (the backend closes the question) and `advance` elsewhere. Voice
 * mode has no composer, so this is the only send path there; reading `thread.draft` alone would
 * see the recorded primary answer, so `isAlreadySubmitted`/`isForwardAdvanceAllowed` would refuse
 * to move — a dead end with the dictated reply stuck in `followUpDraft`. Routes through
 * `resolveActiveDraft`/`isActiveDraftComplete`, the same pair `onSubmitAnswer` reads, so page
 * Continue and composer Send stay byte-identical.
 */
function resolveFollowUpReplyIntent(
  state: ResponderSessionState,
  thread: ResponderThread
): ForwardIntent {
  const { questionId, requestSeq, threadId } = thread;
  const reply = resolveActiveDraft(state, questionId);

  if (reply === null || !isActiveDraftComplete(state, questionId)) {
    return selectIsAwaitingVoiceFollowUpReply(state)
      ? { type: 'skip', questionId, threadId, requestSeq: requestSeq + 1 }
      : { type: 'advance' };
  }

  return {
    type: 'turn',
    questionId,
    threadId,
    answer: reply,
    requestSeq: requestSeq + 1,
    edit: null,
  };
}

/**
 * Whether resolving this intent already puts a request on the wire. `turn`/`skip` reach
 * `gateway.addQuestionMessage`/`skipQuestion`; `advance` never does, since `isForwardAdvanceAllowed`
 * only returns it once the thread is `Closed`. A finishing Continue needs this to know whether it
 * can ask `surveyCompletionStatus` immediately or must wait for an in-flight request first —
 * asking it alongside an unresolved `skipQuestion` races the backend into concluding the survey
 * ended before that skip settles it.
 */
export function isForwardIntentInFlight(intent: ForwardIntent): boolean {
  return intent.type !== 'advance';
}

/**
 * Whether the responder may leave the current question unanswered, whatever the draft holds.
 * `resolveForwardIntent` answers `skip` exactly when this holds and the draft is incomplete.
 */
export function isCurrentQuestionSkippable(
  state: ResponderSessionState
): boolean {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);

  if (spec === null || thread === null || spec.isRequired) {
    return false;
  }

  if (state.editing.mode === 'editing' || selectIsBlockedBySiteGate(state)) {
    return false;
  }

  return (
    !isReplyingToFollowUp(state, thread.questionId) &&
    !isAlreadySubmitted(state, thread)
  );
}

export function resolveForwardEdit(
  state: ResponderSessionState,
  thread: ResponderThread
): ForwardEdit | null {
  const isEditing =
    state.editing.mode === 'editing' &&
    state.editing.questionId === thread.questionId;

  return isEditing && thread.answerMessageId !== null
    ? { messageId: thread.answerMessageId, isBubbleRewrite: false }
    : null;
}

export function resolveForwardIntent(
  state: ResponderSessionState
): ForwardIntent {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);

  if (spec === null || thread === null) {
    return { type: 'advance' };
  }

  /**
   * A gate-blocked Task/Url must never reach the skip below: `skipQuestion` would complete the
   * question on the backend while `isForwardAdvanceAllowed` still refuses to move the local
   * cursor, desyncing the two forever. `advance` sends nothing instead.
   */
  if (state.editing.mode !== 'editing' && selectIsBlockedBySiteGate(state)) {
    return { type: 'advance' };
  }

  /**
   * An open follow-up outranks the question's own answer: `isAlreadySubmitted` below would
   * otherwise read the recorded primary answer and refuse to produce any turn.
   */
  if (isReplyingToFollowUp(state, thread.questionId)) {
    return resolveFollowUpReplyIntent(state, thread);
  }

  if (isAlreadySubmitted(state, thread)) {
    return { type: 'advance' };
  }

  const { draft, questionId, requestSeq, threadId } = thread;
  const nextRequestSeq = requestSeq + 1;
  const hasCompleteDraft = getThreadBehaviour(thread.kind).isDraftComplete(
    draft,
    spec
  );

  /**
   * An edit with an unfinished draft has nothing to replace the old answer with, and must never
   * fall through to the skip below — that would throw away the answer being changed.
   */
  if (state.editing.mode === 'editing' && !hasCompleteDraft) {
    return { type: 'advance' };
  }

  if (hasCompleteDraft && draft !== null) {
    return {
      type: 'turn',
      questionId,
      threadId,
      answer: draft,
      requestSeq: nextRequestSeq,
      edit: resolveForwardEdit(state, thread),
    };
  }

  const isDeliberateSkip =
    !hasCompleteDraft && !spec.isRequired && !isOtherTextMissing(draft);

  return isDeliberateSkip
    ? { type: 'skip', questionId, threadId, requestSeq: nextRequestSeq }
    : { type: 'advance' };
}
